// WHAT: Main application component for SwitchBot device management
// WHY:  Provides interface for viewing and managing SwitchBot devices with status monitoring
// DOMAIN: SmartHome → SwitchBot
// DEPENDENCIES: SwitchBot services, SwitchBotDeviceCard, HubStatusList components
// RISKS:
//   - NOTE: Device filtering logic is complex - may incorrectly classify active/inactive devices
//   - NOTE: Status fetching errors are silently ignored - devices shown without status
//   - NOTE: Uses index as key in map - may cause issues if devices array changes

import { useState, useEffect } from "react";

import { Button } from "@/components/ui";
import {
  fetchSwitchBotDevices,
  fetchSwitchBotDeviceStatus,
  type SwitchBotDeviceStatus,
} from "@/domains/smarthome/services";

import { SwitchBotDeviceCard, HubStatusList } from "./components";

/**
 * Lokale, strikt getypte SwitchBot-Device-Repräsentation (unabhängig von
 * eventuell schwach typisierten Service-Typen).
 */
interface SwitchBotDeviceLike {
  deviceId: string;
  deviceName: string;
  deviceType: string;
  hubDeviceId: string;
}

/**
 * WHAT:
 *   Runtime type guard that validates an unknown value as a SwitchBotDeviceLike.
 *
 * WHY:
 *   External SwitchBot service types may be loose/any-typed; this guard ensures
 *   we only treat structurally valid objects as SwitchBotDeviceLike, satisfying
 *   strict linting rules.
 *
 * @param value
 *
 * @returns {value is SwitchBotDeviceLike} True if value is a valid SwitchBotDeviceLike
 *
 */
function isSwitchBotDevice(value: unknown): value is SwitchBotDeviceLike {
  if (!value || typeof value !== "object") {
    return false;
  }

  const record = value as Record<string, unknown>;
  return (
    typeof record.deviceId === "string" &&
    typeof record.deviceType === "string" &&
    typeof record.deviceName === "string"
  );
}

/**
 * Local, runtime-validated sensor status shape to avoid relying on `any` from
 * external libraries and to satisfy strict ESLint no-unsafe-* rules.
 */
interface SensorStatus {
  battery?: number;
  humidity?: number;
  temperature?: number;
  version?: string;
}

/**
 * Helper function to check if value is number or undefined
 *
 * @param v - Value to check
 *
 * @returns {boolean} True if value is number or undefined
 *
 */
const isNumberOrUndefined = (v: unknown): boolean => typeof v === "number" || v === undefined;

/**
 * Helper function to check if value is string or undefined
 *
 * @param v - Value to check
 *
 * @returns {boolean} True if value is string or undefined
 *
 */
const isStringOrUndefined = (v: unknown): boolean => typeof v === "string" || v === undefined;

/**
 * WHAT:
 *   Runtime type guard that validates an unknown value as a SensorStatus.
 *
 * WHY:
 *   External SwitchBot status types may be loosely typed (e.g. `any`), which
 *   violates strict linting rules when accessing members directly. This guard
 *   ensures we only treat structurally valid objects as sensor status.
 *
 * RISKS:
 *   - Non-standard payloads from the API might be filtered out if they don't
 *     follow the expected shape (numbers / strings or undefined).
 *
 * @param value
 *
 * @returns {value is SensorStatus} True if value is a valid SensorStatus
 *
 */
function isSensorStatus(value: unknown): value is SensorStatus {
  if (!value || typeof value !== "object") {
    return false;
  }

  const record = value as Record<string, unknown>;

  return (
    isNumberOrUndefined(record.temperature) &&
    isNumberOrUndefined(record.humidity) &&
    isNumberOrUndefined(record.battery) &&
    isStringOrUndefined(record.version)
  );
}

/**
 * Extended device interface with optional status, basierend auf der lokalen
 * SwitchBotDeviceLike-Definition.
 */
interface DeviceWithStatus extends SwitchBotDeviceLike {
  /** Optional device status (sensor readings, battery, etc.) */
  status?: SensorStatus;
}

type FetchDevicesFunction = () => Promise<unknown>;
type FetchStatusFunction = (deviceId: string) => Promise<unknown>;

const safeFetchSwitchBotDevices: FetchDevicesFunction =
  fetchSwitchBotDevices as FetchDevicesFunction;
const safeFetchSwitchBotDeviceStatus: FetchStatusFunction =
  fetchSwitchBotDeviceStatus as FetchStatusFunction;

/**
 * WHAT:
 *   Main application component for managing and displaying SwitchBot devices
 *   with automatic device loading and status monitoring.
 *
 * WHY:
 *   Provides a unified interface for viewing all SwitchBot devices, their status,
 *   and sensor data in a organized grid layout.
 *
 * BEHAVIOR:
 *   - Automatically loads devices on mount
 *   - Fetches status for WoIOSensor devices
 *   - Filters devices into hubs and sensors
 *   - Categorizes sensors as active/inactive based on data validity
 *   - Provides toggle to show/hide inactive devices
 *   - Displays devices in responsive grid
 *   - Shows hub status list overlay
 *
 * RISKS:
 *   - NOTE: Device filtering logic is complex - may incorrectly classify devices
 *   - NOTE: Status fetching errors are silently ignored - devices shown without status
 *   - NOTE: Active/inactive classification may not match user expectations
 *   - NOTE: Uses index as key - should use unique device identifier
 *
 * @returns {JSX.Element} SwitchBot application component
 *
 */
export function SwitchBotApp() {
  // NOTE: Loading state during device fetch
  const [loading, setLoading] = useState<boolean>(false);
  // NOTE: Status message for user feedback
  const [message, setMessage] = useState<null | string>(null);
  // NOTE: State for all devices with optional status
  const [devices, setDevices] = useState<DeviceWithStatus[]>([]);
  // NOTE: State for showing/hiding inactive devices
  const [showInactiveDevices, setShowInactiveDevices] = useState<boolean>(false);

  // NOTE: Automatisch Geräte beim Laden abrufen
  useEffect(() => {
    void fetchDevices();
  }, []);

  // NOTE: Filtere Hubs und Sensoren - Hubs enthalten "Hub" im Typ
  const hubs: DeviceWithStatus[] = devices.filter((device) => device.deviceType.includes("Hub"));
  // NOTE: Sensoren sind alle Geräte, die keine Hubs sind
  const sensors: DeviceWithStatus[] = devices.filter(
    (device) => !device.deviceType.includes("Hub"),
  );

  /**
   * WHAT:
   *   Checks if a sensor device has valid status data.
   *
   * WHY:
   *   Identifies sensors that are actively reporting data vs. inactive/unregistered sensors.
   *
   * BEHAVIOR:
   *   - Requires device status to exist
   *   - Checks for valid temperature (>0), humidity (>0), battery (>0), or version (!= "V0.0")
   *   - Returns true if any valid data exists
   *
   * RISKS:
   *   - NOTE: Validation logic may incorrectly classify devices
   *   - NOTE: Temperature/humidity >0 check may exclude valid negative values
   *
   * @param device - Device to check
   *
   * @returns {boolean} True if device has valid status data
   *
   */
  const hasValidSensorStatus = (device: DeviceWithStatus): boolean => {
    if (!device.status) {
      return false;
    } // NOTE: Kein Status = nicht registriert

    const status = device.status;
    // NOTE: Prüfe auf gültige Sensor-Daten
    const hasValidTemperature = status.temperature !== undefined && status.temperature > 0;
    const hasValidHumidity = status.humidity !== undefined && status.humidity > 0;
    const hasValidBattery = status.battery !== undefined && status.battery > 0;
    const hasValidVersion = !!(status.version && status.version !== "V0.0");

    return hasValidTemperature || hasValidHumidity || hasValidBattery || hasValidVersion;
  };

  /**
   * WHAT:
   *   Filters sensors that have valid status data.
   *
   * WHY:
   *   Identifies sensors that are actively reporting data vs. inactive/unregistered sensors.
   */
  const activeSensors: DeviceWithStatus[] = sensors.filter((device) =>
    hasValidSensorStatus(device),
  );

  /**
   * WHAT:
   *   Filters sensors that don't have valid status data.
   *
   * WHY:
   *   Identifies inactive or unregistered sensors that should be hidden by default.
   *
   * BEHAVIOR:
   *   - Returns true if no status exists
   *   - Returns true if all data is invalid (opposite of activeSensors logic)
   *
   * RISKS:
   *   - NOTE: Validation logic may incorrectly classify devices
   *   - NOTE: Devices without status are always considered inactive
   */
  const inactiveSensors: DeviceWithStatus[] = sensors.filter((device) => {
    if (!device.status) {
      return true;
    } // NOTE: Kein Status = inaktiv

    const status = device.status;
    // NOTE: Prüfe auf ungültige Sensor-Daten
    const hasValidTemperature = status.temperature !== undefined && status.temperature > 0;
    const hasValidHumidity = status.humidity !== undefined && status.humidity > 0;
    const hasValidBattery = status.battery !== undefined && status.battery > 0;
    const hasValidVersion = status.version && status.version !== "V0.0";

    return !(hasValidTemperature || hasValidHumidity || hasValidBattery || hasValidVersion);
  });

  const displaySensors: DeviceWithStatus[] = showInactiveDevices ? sensors : activeSensors;

  /**
   * WHAT:
   *   Fetches all SwitchBot devices and their status (for WoIOSensor devices).
   *
   * WHY:
   *   Loads device list and sensor data for display in the application.
   *
   * BEHAVIOR:
   *   - Fetches device list from SwitchBot API
   *   - Fetches status for WoIOSensor devices only
   *   - Handles errors gracefully (devices shown without status if fetch fails)
   *   - Updates state with devices and success message
   *
   * RISKS:
   *   - NOTE: Status fetching errors are silently ignored - devices shown without status
   *   - NOTE: Error message assumes credentials issue - may not be accurate
   *   - NOTE: No retry mechanism for failed status fetches
   */
  const fetchDevices = async () => {
    setLoading(true);
    setMessage(null);

    try {
      // Fetch devices from service (may return unknown/any - we validate below)
      const rawDevices = await safeFetchSwitchBotDevices();

      // Validate devices are properly typed
      const validatedDevices: SwitchBotDeviceLike[] = Array.isArray(rawDevices)
        ? rawDevices.filter((device) => isSwitchBotDevice(device))
        : [];

      // NOTE: Status für WoIOSensor-Geräte abrufen (nicht für Hub-Geräte)
      const devicesWithStatus: DeviceWithStatus[] = await Promise.all(
        validatedDevices.map(async (device) => {
          const deviceWithStatus: DeviceWithStatus = {
            deviceId: device.deviceId,
            deviceName: device.deviceName,
            deviceType: device.deviceType,
            hubDeviceId: device.hubDeviceId,
          };

          // NOTE: Nur für WoIOSensor-Geräte Status abrufen
          if (device.deviceType === "WoIOSensor") {
            try {
              const rawStatus = await safeFetchSwitchBotDeviceStatus(device.deviceId);
              if (isSensorStatus(rawStatus)) {
                deviceWithStatus.status = rawStatus;
              }
            } catch {
              // NOTE: Status konnte nicht abgerufen werden - Status bleibt undefined, Gerät wird trotzdem angezeigt
            }
          } else {
            // NOTE: Hub-Geräte benötigen keinen Status
          }

          return deviceWithStatus;
        }),
      );

      setDevices(devicesWithStatus);
      setMessage(`✅ ${String(validatedDevices.length)} Geräte gefunden`);
    } catch {
      // NOTE: Failed to fetch SwitchBot devices - show error message
      setMessage(
        "❌ Fehler beim Abrufen der Geräte. Bitte überprüfen Sie Ihre Credentials in den Settings.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">SwitchBot</h1>
        <p className="text-slate-400">Verwalte deine SwitchBot-Geräte.</p>
      </div>

      <div className="bg-slate-900 rounded-lg p-6 space-y-6">
        {/* Device Management */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-200">Geräte</h2>
              <p className="text-slate-400 text-sm">
                Deine SwitchBot-Geräte werden automatisch geladen.
              </p>
            </div>
            {inactiveSensors.length > 0 && (
              <Button
                onClick={() => {
                  setShowInactiveDevices(!showInactiveDevices);
                }}
                size="sm"
                variant="secondary"
              >
                {showInactiveDevices
                  ? "Nur aktive anzeigen"
                  : `${String(inactiveSensors.length)} inaktive anzeigen`}
              </Button>
            )}
          </div>

          {loading ? <div className="text-sm text-slate-400">Lade Geräte...</div> : null}

          {displaySensors.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-slate-300">
                  {showInactiveDevices ? "Alle Geräte" : "Aktive Geräte"}
                </h3>
                <div className="text-sm text-slate-400">
                  {String(activeSensors.length)} aktiv
                  {inactiveSensors.length > 0 && ` • ${String(inactiveSensors.length)} inaktiv`}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displaySensors.map((device: DeviceWithStatus) => (
                  <SwitchBotDeviceCard
                    device={device}
                    // Reason: TypeScript cannot infer property types from DeviceWithStatus interface in map callback
                    // The interface is correctly defined, but type inference fails in this context
                    key={device.deviceId}
                    {...(device.status
                      ? {
                          status: {
                            ...device.status,
                            deviceId: device.deviceId,
                            deviceType: device.deviceType,
                            hubDeviceId: device.hubDeviceId || "",
                          } as SwitchBotDeviceStatus,
                        }
                      : {})}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Status Messages */}
        {message ? (
          <div className="text-sm text-slate-400 bg-slate-800 rounded p-3">{message}</div>
        ) : null}
      </div>

      {/* Hub Status List */}
      <HubStatusList hubs={hubs} />
    </div>
  );
}
